import AutocompleteFormField from "@/components/Common/AutocompleteFormField";
import SEOMetadata from "@/components/Common/SEOMetadata";
import { SectionCard, WorkspaceFieldLabel } from '@components/Common/EntityWorkspace';
import { PageHero } from '@components/Common/Panel';
import ErrorMessage from "@components/Common/ErrorMessage";
import FileUpload from "@components/Common/FileUpload/FileUpload";
import FormField from "@components/Common/FormField";
import Loader from "@components/Common/Loader";
import LocationAutocomplete, { AddressDetails } from "@components/Common/LocationAutocomplete";
import PhoneInput from "@components/Common/PhoneInput";
import SelectFormField from "@components/Common/SelectFormField";
import { TextEditor } from "@components/Common/TextEditor/TextEditor";
import { createCompanyQuery, getActiveValuesByTypeQuery } from "@constants/graphQLQueries";
import { fetchGraphQLApiData } from "@helpers/graphQLApi";
import Add from "@mui/icons-material/Add";
import {
    Box,
    Button,
    Chip,
    FormControlLabel,
    IconButton,
    Switch,
    Typography
} from "@mui/material";
import { uploadToS3Direct } from '@components/Common/directUpload';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CompanyFormData } from "../types";
import './index.scss';

/*
 * UPPERCASE, matching the Java service.
 *
 * These were lowercase while CompanyService.java:143 writes `company.setStatus("ACTIVE")` on
 * creation, so the collection ended up holding both: 1,618 `active` against 17 `ACTIVE`, plus
 * lowercase `pending`/`rejected`/`declined`. Two writers, two conventions, one field.
 *
 * That is why every status comparison in the query layer is a case-insensitive regex
 * (`Criteria.where("status").regex("^ACTIVE$", "i")` in CompanyService.getAllFilteredCompanyPage,
 * and the `^DECLINED$` exclusion in JobsAggregateService). Those regexes are load-bearing against
 * the mixed data, and a case-insensitive regex can never use an index — so the inconsistency also
 * pins those queries to a collection scan for good.
 *
 * Uppercase wins because it is what the backend writes and what jobStatus already uses
 * (OPEN/CLOSED/EXPIRED). The existing rows are normalised separately; the regexes stay until that
 * has landed everywhere, because tightening them first would hide every lowercase row.
 */
const ApprovalStatus = [
    { value: 'REJECTED', label: 'Rejected' },
    { value: 'ACTIVE', label: 'Active' },
    { value: 'PENDING', label: 'Pending' },
    // DECLINED — admin take-down. Hidden from all public detail queries.
    { value: 'DECLINED', label: 'Declined' },
];

const INDUSTRIES = [
    'Jewellery',
    'Luxury Retail',
    'Precious Metals',
    'Mining',
    'Gemstone',
    'D2C Brands',
];

const CompanyCreatePage: React.FC = () => {
    const [formData, setFormData] = useState<CompanyFormData>({
        id: '',
        companyEmail: '',
        companyName: '',
        website: '',
        instagramURL: '',
        linkedInURL: '',
        facebook: '',
        behance: '',
        googleDrive: '',
        twitter: '',
        location: '',
        description: '',
        logoUrl: '',
        logo: '',
        country: '',
        city: '',
        zipCode: '',
        state: '',
        phoneNumber: '',
        industry: '',
        benefits: [],
        skills: [],
        employers: [],
        jobsPosted: [],
        tags: [],
        salesOwnerName: '',
        isVerified: false,
        isFeatured: false,
        metadata: {
            title: '',
            url: '',
            description: '',
            keywords: '',
            metadata: [],
        },
        status: '',
        createdAt: '',
        createdBy: '',
        updatedAt: '',
        updatedBy: '',
        locality: '',
        placeId: '',
        lat: null,
        lng: null,
        notes: [],
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [skillInput, setSkillInput] = useState('');
    const [benefitInput, setBenefitInput] = useState('');
    const [tagInput, setTagInput] = useState('');
    const [skillOptions, setSkillOptions] = useState<string[]>([]);
    const navigate = useNavigate();

    useEffect(() => {
        fetchGraphQLApiData(getActiveValuesByTypeQuery('SKILL'))
            .then((res) => {
                const values: string[] = res?.data?.getActiveValuesByType ?? [];
                setSkillOptions(values);
            })
            .catch(() => {});
    }, []);

    const handleRemoveSkill = (index: number) => {
        const updated = [...formData.skills];
        updated.splice(index, 1);
        setFormData(prev => ({ ...prev, skills: updated }));
    };

    const handleRemoveBenefit = (index: number) => {
        const updated = [...formData.benefits];
        updated.splice(index, 1);
        setFormData(prev => ({ ...prev, benefits: updated }));
    };

    const addSkill = (val?: string) => {
        const v = (val ?? skillInput).trim();
        if (v && !formData.skills.includes(v)) {
            setFormData(prev => ({ ...prev, skills: [...prev.skills, v] }));
            setSkillInput('');
        }
    };

    const addBenefit = () => {
        const v = benefitInput.trim();
        if (v && !formData.benefits.includes(v)) {
            setFormData(prev => ({ ...prev, benefits: [...prev.benefits, v] }));
            setBenefitInput('');
        }
    };

    /*
     * Tags were in the form state, in the create mutation and on the edit screen, and had no control
     * here: `tags: []` went out on every company created in the Control Center. They are how a
     * company is grouped internally, so a company created here started ungrouped and nobody knew to
     * go back for it.
     */
    const addTag = () => {
        const v = tagInput.trim();
        if (v && !formData.tags.includes(v)) {
            setFormData(prev => ({ ...prev, tags: [...prev.tags, v] }));
            setTagInput('');
        }
    };
    const handleRemoveTag = (index: number) => {
        setFormData(prev => ({ ...prev, tags: prev.tags.filter((_, i) => i !== index) }));
    };

    const handleFileUpload = (data: File | '') => {
        setFormData((prev) => ({ ...prev, logo: data }));
    };

    const setTextEditorValue = (value: string) => {
        setFormData((prev) => ({ ...prev, description: value }));
    };

    const validateForm = () => {
        const tempErrors: { [key: string]: string } = {};
        if (!formData.companyName) tempErrors.companyName = 'Company Name is required';
        if (!formData.companyEmail || !/\S+@\S+\.\S+/.test(formData.companyEmail)) {
            tempErrors.companyEmail = 'Valid email is required';
        }
        if (
            !formData.website ||
            !/\./.test(formData.website) ||
            /\s/.test(formData.website)
        ) {
            tempErrors.website = 'Website must be valid';
        }
        if (formData.zipCode && !/^\d+$/.test(formData.zipCode)) {
            tempErrors.zipCode = 'ZIP code must be numeric';
        }
        if (formData.status === '') tempErrors.status = 'Approval Status is required';
        if (!formData.industry) tempErrors.industry = 'Industry is required';
        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (validateForm()) {
            setLoading(true);
            setError(null);
            try {
                if (formData.logo instanceof File) {
                    const fileName = `${formData.companyName}_${Date.now()}_${formData.logo.name}`;
                    // Direct-to-S3 presigned POST; the logo bucket is public-read (the policy
                    // decides). Build the public URL from the server's key, encoded for spaces.
                    const key = await uploadToS3Direct('trymintly-companylogo', formData.logo, {
                        fileName,
                    });
                    formData.logoUrl = `https://trymintly-companylogo.s3.ap-south-1.amazonaws.com/${encodeURIComponent(key)}`;
                }
                const query = createCompanyQuery(formData);
                await fetchGraphQLApiData(query, { showStatus: true, message: 'Company created' });
                navigate('/companies');
            } catch (err) {
                setError('Failed to create company. Please try again.');
            } finally {
                setLoading(false);
            }
        }
        else {
            setError('Please fix the validation errors before submitting.');
        }
    };

    return (
        <Box className="cc-create">
            <PageHero
                kicker="New company"
                title="Add a company to the platform."
                text="Only the name is required. Everything else can be filled in later from the company's workspace."
                actions={(
                    <>
                        <Button variant="outlined" onClick={() => navigate(-1)} disabled={loading}>
                            Cancel
                        </Button>
                        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
                            {loading ? 'Creating…' : 'Create company'}
                        </Button>
                    </>
                )}
            />

            {loading && <Loader />}
            {error && <ErrorMessage message={error} />}

                <Box className="cc-create__cards">
                    {/* Basic Information */}
                    <SectionCard title="Basic Information">
                        <FormField
                            required
                            errors={errors}
                            formData={formData}
                            label="Company Name"
                            name="companyName"
                            onChange={(name, value) => setFormData(prev => ({ ...prev, [name]: value }))}
                        />
                        <FormField
                            required
                            errors={errors}
                            formData={formData}
                            label="Company Email"
                            name="companyEmail"
                            type="email"
                            onChange={(name, value) => setFormData(prev => ({ ...prev, [name]: value }))}
                        />
                        {/*
                          * Internal only, and redacted for a non-admin reader alongside
                          * discoveryAnswers. It was on CompanyUpdateInput and not on
                          * CompanyCreateInput, so the only way to attach an owner was to create the
                          * company and then edit it, and in practice nobody went back.
                          */}
                        <FormField
                          required={false}
                            formData={formData}
                            label="Sales / BD owner"
                            name="salesOwnerName"
                            placeholder="Who sourced this account"
                            onChange={(name, value) => setFormData(prev => ({ ...prev, [name]: value }))}
                        />
                        <SelectFormField
                          required
                            label="Approval Status"
                            name="status"
                            options={ApprovalStatus}
                            value={formData.status}
                            error={!!errors.status}
                            errors={errors}
                            onChange={(_, value) => setFormData(prev => ({ ...prev, status: value as string }))}
                        />
                        <AutocompleteFormField
                          required
                            className="text-field"
                            inputValue={formData.industry}
                            error={!!errors.industry}
                            helperText={errors.industry}
                            label="Industry"
                            name="industry"
                            options={INDUSTRIES}
                            value={formData.industry}
                            onChange={(_event, newValue) => setFormData(prev => ({ ...prev, industry: newValue || '' }))}
                            onInputChange={(_event, newInputValue) => setFormData(prev => ({ ...prev, industry: newInputValue }))}
                        />
                        {/*
                            Both toggles in one full-width row, not two narrow columns.

                            They were `md: 2` inside an MUI Grid, whose breakpoints measure the
                            viewport while this card is half of it: a twelfth of half a page is
                            about 100px, which is a switch and most of a word, so "Featured" ran
                            off the card edge. The Grid is gone now and the card's own
                            `repeat(auto-fit, minmax(190px, 1fr))` sizes to the card, but a pair
                            of switches still belongs on one row rather than in two field cells.
                        */}
                        <Box className="cc-field--full" sx={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-lg)' }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={!!formData.isVerified}
                                        onChange={(e) => setFormData(prev => ({ ...prev, isVerified: e.target.checked }))}
                                    />
                                }
                                label="Verified"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={!!formData.isFeatured}
                                        onChange={(e) => setFormData(prev => ({ ...prev, isFeatured: e.target.checked }))}
                                    />
                                }
                                label="Featured"
                            />
                        </Box>
                    </SectionCard>

                    {/* Address */}
                    <SectionCard title="Address">
                        {/* One control that draws five fields of its own. It has to own the row. */}
                        <Box className="cc-field--full">
                            <LocationAutocomplete
                                city={formData.city}
                                state={formData.state}
                                country={formData.country}
                                locality={formData.locality}
                                zipCode={formData.zipCode}
                                onChange={(name, value) => setFormData(prev => ({ ...prev, [name]: value }))}
                                onAddressChange={(details: AddressDetails) => setFormData(prev => ({
                                    ...prev,
                                    locality: details.locality,
                                    city: details.city,
                                    state: details.state,
                                    country: details.country,
                                    placeId: details.placeId,
                                    lat: details.lat,
                                    lng: details.lng,
                                    location: [details.city, details.state, details.country].filter(Boolean).join(', '),
                                }))}
                            />
                        </Box>
                        <PhoneInput
                          required={false}
                            fullWidth
                            label="Phone Number"
                            value={formData.phoneNumber}
                            onChange={(val) => setFormData(prev => ({ ...prev, phoneNumber: val }))}
                        />
                    </SectionCard>

                    {/* Social Links */}
                    <SectionCard title="Social Links">
                        <FormField
                            required
                            errors={errors}
                            formData={formData}
                            label="Website"
                            name="website"
                            onChange={(name, value) => setFormData(prev => ({ ...prev, [name]: value }))}
                        />
                        <FormField
                          required={false}
                            errors={errors}
                            formData={formData}
                            label="Instagram"
                            name="instagramURL"
                            onChange={(name, value) => setFormData(prev => ({ ...prev, [name]: value }))}
                        />
                        <FormField
                          required={false}
                            errors={errors}
                            formData={formData}
                            label="LinkedIn"
                            name="linkedInURL"
                            onChange={(name, value) => setFormData(prev => ({ ...prev, [name]: value }))}
                        />
                    </SectionCard>

                    {/* Company Logo */}
                    <SectionCard title="Company Logo">
                        <FileUpload
                            disabled={false}
                            file={formData.logo}
                            maxFileSize={2 * 1024 * 1024}
                            supportedFileTypes=".png,.jpeg,.jpg"
                            title="Upload Logo"
                            onFileDrop={handleFileUpload}
                        />
                    </SectionCard>

                    {/* Skills & Benefits */}
                    {/* Spans the row. Chips read better across the full measure, and it makes the
                        card count before Description even, so those two pair instead of leaving
                        SEO Metadata stranded on a row of its own. */}
                    <SectionCard className="cc-create__card--wide" title="Skills & Benefits">
                        <Box className="cc-field--full">
                            <WorkspaceFieldLabel>Skills</WorkspaceFieldLabel>
                            <Box className="cc-field-row">
                                <AutocompleteFormField
                                  required={false}
                                    freeSolo
                                    fullWidth
                                    inputValue={skillInput}
                                    label="Add Skill"
                                    name="new-skill"
                                    options={skillOptions.filter(o => !formData.skills.includes(o))}
                                    value={null}
                                    onChange={(_, newValue, reason) => {
                                        if (reason === 'selectOption') {
                                            addSkill(typeof newValue === 'string' ? newValue : '');
                                        }
                                    }}
                                    onInputChange={(_, newInputValue) => setSkillInput(newInputValue)}
                                />
                                <IconButton color="primary" size="small" onClick={() => addSkill()}>
                                    <Add />
                                </IconButton>
                            </Box>
                            <Box className="skills-chips-container">
                                {formData.skills.map((skill, index) => (
                                    skill.trim() && <Chip key={index} className="chip-muted" label={skill} size="small" onDelete={() => handleRemoveSkill(index)} />
                                ))}
                                {formData.skills.length === 0 && <Typography className="muted-text" variant="body2">Start typing to add skills</Typography>}
                            </Box>
                        </Box>
                        <Box className="cc-field--full">
                            <WorkspaceFieldLabel>Benefits</WorkspaceFieldLabel>
                            <Box className="cc-field-row">
                                <FormField
                                  required={false}
                                    fullWidth
                                    label="Add Benefit"
                                    name="benefitInput"
                                    value={benefitInput}
                                    onChange={(_, v) => setBenefitInput(v)}
                                    textFieldProps={{
                                        onKeyDown: (e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                addBenefit();
                                            }
                                        }
                                    }}
                                />
                                <IconButton color="primary" size="small" onClick={addBenefit}>
                                    <Add />
                                </IconButton>
                            </Box>
                            <Box className="skills-chips-container">
                                {formData.benefits.map((benefit, index) => (
                                    benefit.trim() && <Chip key={index} className="chip-muted" label={benefit} size="small" onDelete={() => handleRemoveBenefit(index)} />
                                ))}
                                {formData.benefits.length === 0 && <Typography className="muted-text" variant="body2">Start typing to add benefits</Typography>}
                            </Box>
                        </Box>
                        <Box className="cc-field--full">
                            <WorkspaceFieldLabel>Tags</WorkspaceFieldLabel>
                            <Box className="cc-field-row">
                                <FormField
                                  required={false}
                                    fullWidth
                                    label="Add Tag"
                                    name="tagInput"
                                    value={tagInput}
                                    onChange={(_, v) => setTagInput(v)}
                                    textFieldProps={{
                                        onKeyDown: (e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                addTag();
                                            }
                                        }
                                    }}
                                />
                                <IconButton color="primary" size="small" onClick={addTag}>
                                    <Add />
                                </IconButton>
                            </Box>
                            <Box className="skills-chips-container">
                                {formData.tags.map((tag, index) => (
                                    tag.trim() && <Chip key={index} className="chip-muted" label={tag} size="small" onDelete={() => handleRemoveTag(index)} />
                                ))}
                                {formData.tags.length === 0 && <Typography className="muted-text" variant="body2">Start typing to add tags</Typography>}
                            </Box>
                        </Box>
                    </SectionCard>

                {/* Description and SEO sit in the grid with everything else, rather than as two
                    full-width rows below a two-column form. */}
                <SectionCard title="Company Description">
                    <TextEditor readOnly={false} setValue={setTextEditorValue} value={formData.description} />
                </SectionCard>

                <SectionCard title="SEO Metadata">
                    <SEOMetadata
                        data={formData.metadata}
                        onChange={(updatedMetadata) => setFormData(prev => ({ ...prev, metadata: updatedMetadata }))}
                    />
                </SectionCard>
                </Box>
        </Box>
    );
};

export default CompanyCreatePage;
